import { Eye, FileSpreadsheet, FileText, Mail, Paperclip } from 'lucide-react';
import type { EmailEnvelope, SourceFileRef } from '@/services/ingestion';
import SourcePreviewPane from '@/components/intake/SourcePreviewPane';
import { ORIGINATION_MAILBOX, formatReceivedDisplay } from '@/components/intake/EmailedIntakeTable';

interface AttachmentChip {
  filename: string;
  type: string;
  sizeKb: number;
  parsed?: boolean;
  sha256?: string;
}

interface IngestedCommunicationPanelProps {
  ingestionId: string;
  from: string;
  subject: string;
  receivedAt: string;
  recipient?: string;
  broker?: string;
  brokerFirm?: string;
  emailBody?: string | null;
  attachments?: AttachmentChip[];
  emailEnvelope?: EmailEnvelope | null;
  rawFilePaths?: SourceFileRef[];
  activeSha?: string | null;
  onOpenSource?: (sha: string) => void;
  onOpenAttachment?: (att: AttachmentChip) => void;
}

function attachmentIcon(type: string, filename: string) {
  if (type === 'excel' || /\.(xlsx|xlsm|csv)$/i.test(filename)) {
    return <FileSpreadsheet size={11} className="shrink-0 text-emerald-400" />;
  }
  if (type === 'pdf' || /\.pdf$/i.test(filename)) {
    return <FileText size={11} className="shrink-0 text-red-400" />;
  }
  return <FileText size={11} className="shrink-0 text-blue-400" />;
}

function resolveChipSource(
  att: AttachmentChip,
  refs: SourceFileRef[] | undefined,
): SourceFileRef | null {
  const list = refs ?? [];
  const lower = att.filename.toLowerCase();
  const byName =
    list.find((f) => f.filename === att.filename) ??
    list.find((f) => f.filename.toLowerCase() === lower) ??
    list.find(
      (f) =>
        f.filename.toLowerCase().includes(lower) ||
        lower.includes(f.filename.toLowerCase()),
    );
  if (byName) return byName;
  if (att.sha256) {
    return { filename: att.filename, kind: 'attachment', sha256: att.sha256 };
  }
  return null;
}

export default function IngestedCommunicationPanel({
  ingestionId,
  from,
  subject,
  receivedAt,
  recipient,
  broker,
  brokerFirm,
  emailBody,
  attachments = [],
  emailEnvelope,
  rawFilePaths,
  activeSha,
  onOpenSource,
  onOpenAttachment,
}: IngestedCommunicationPanelProps) {
  const to = recipient || emailEnvelope?.to?.filter(Boolean).join(', ') || ORIGINATION_MAILBOX;
  const emailFile = rawFilePaths?.find((f) => f.kind === 'email_source');
  const hasStoredSource = (rawFilePaths?.length ?? 0) > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="shrink-0 space-y-1 rounded-lg border border-amber-400/10 bg-black/25 px-3 py-2">
        <HeaderRow label="From" value={emailEnvelope?.fromEmail || from} />
        <HeaderRow label="To" value={to} mono />
        <HeaderRow label="Subject" value={emailEnvelope?.subject || subject} />
        <HeaderRow label="Received" value={formatReceivedDisplay(receivedAt)} />
        {broker ? (
          <HeaderRow label="Broker" value={`${broker}${brokerFirm ? ` · ${brokerFirm}` : ''}`} />
        ) : null}
      </div>

      {attachments.length > 0 && (
        <div className="shrink-0">
          <div className="mb-1.5 flex items-center gap-1.5">
            <Paperclip size={11} className="text-stone-500" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-stone-500">
              Attachments ({attachments.length}) — click to review
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {attachments.map((att) => {
              const match = resolveChipSource(att, rawFilePaths);
              const canOpen = Boolean(onOpenAttachment || (match && onOpenSource));
              if (!canOpen) {
                return (
                  <div
                    key={att.filename}
                    className="flex items-center gap-1.5 rounded-lg border border-amber-400/10 bg-black/20 px-2 py-1 text-[10px] text-stone-300"
                    title={att.filename}
                  >
                    {attachmentIcon(att.type, att.filename)}
                    <span className="max-w-[180px] truncate">{att.filename}</span>
                    {att.sizeKb ? (
                      <span className="font-mono text-stone-600">{att.sizeKb}KB</span>
                    ) : null}
                  </div>
                );
              }
              return (
                <button
                  key={att.filename}
                  type="button"
                  onClick={() => {
                    if (onOpenAttachment) {
                      onOpenAttachment(att);
                      return;
                    }
                    if (match && onOpenSource) onOpenSource(match.sha256);
                  }}
                  className="flex items-center gap-1.5 rounded-lg border border-amber-400/10 bg-black/20 px-2 py-1 text-[10px] text-stone-300 transition-colors hover:border-amber-400/40"
                  title="Open attachment in the source viewer"
                >
                  {attachmentIcon(att.type, att.filename)}
                  <span className="max-w-[180px] truncate">{att.filename}</span>
                  {att.sizeKb ? (
                    <span className="font-mono text-stone-600">{att.sizeKb}KB</span>
                  ) : null}
                  <Eye size={10} className="text-stone-600" />
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="mb-1.5 flex items-center gap-1.5">
          <Mail size={11} className="text-blue-400" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-stone-500">
            Message
          </span>
        </div>
        {hasStoredSource ? (
          <SourcePreviewPane
            ingestionId={ingestionId}
            files={rawFilePaths!}
            activeSha={activeSha ?? emailFile?.sha256 ?? null}
            envelope={emailEnvelope}
            onOpenFull={onOpenSource}
          />
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-stone-800 bg-black/30 px-3 py-3 scrollbar-thin">
            <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-stone-300">
              {emailBody?.trim() || '(No message body was captured for this record.)'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function HeaderRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2 text-[11px]">
      <span className="w-[62px] shrink-0 text-stone-500">{label}</span>
      <span className={`min-w-0 truncate whitespace-nowrap text-stone-200 ${mono ? 'font-mono' : ''}`} title={value}>
        {value}
      </span>
    </div>
  );
}

export function buildDemoEmailBody(req: {
  from: string;
  broker: string;
  brokerFirm: string;
  extracted: {
    property: string;
    type: string;
    location: string;
    amountRequested: string;
    purpose: string;
    sponsor: string;
    unitsSf: string;
    occupancy: string;
    askingCapRate: string;
  };
}): string {
  const capLine =
    req.extracted.askingCapRate !== 'N/A'
      ? ` Asking cap rate is ${req.extracted.askingCapRate}.`
      : '';

  return `Hi team,

Please find attached materials for ${req.extracted.property}, a ${req.extracted.type.toLowerCase()} asset in ${req.extracted.location}.

We are seeking ${req.extracted.amountRequested} in financing for ${req.extracted.purpose.toLowerCase()} on behalf of ${req.extracted.sponsor}. The property comprises ${req.extracted.unitsSf} at ${req.extracted.occupancy} occupancy.${capLine}

Please advise on mandate fit and next steps for triage.

Best,
${req.broker}
${req.brokerFirm}
${req.from}`;
}
